<template>
    <div>
      <!-- Bottom Navigation Bar -->
      <div class="fixed bottom-0 left-0 right-0 bg-white dark:bg-black border-t border-gray-200 dark:border-gray-800 md:hidden">
        <!-- Plus Button -->
        <div class="absolute left-1/2 -translate-x-1/2" style="top: -6px">
          <button
            @click="togglePlusMenu"
            class="w-12 h-12 rounded-full bg-blue-600 text-white shadow-lg flex items-center justify-center transform hover:scale-110 transition-transform"
          >
            <span class="text-2xl">+</span>
          </button>
        </div>
  
        <div class="flex justify-around">
          <button
            v-for="item in mainMenuItems.slice(0, 2)"
            :key="item.id"
            @click="handleMenuClick(item)"
            class="flex-1 py-3 px-2 flex flex-col items-center"
            :class="[
              activeItem === item.id
                ? 'text-blue-600 dark:text-blue-400'
                : 'text-gray-600 dark:text-gray-400'
            ]"
          >
            <IconComponent :name="item.icon" />
            <span class="text-xs">{{ item.label }}</span>
          </button>
  
          <!-- Spacer for plus button -->
          <div class="flex-1"></div>
  
          <button
            v-for="item in mainMenuItems.slice(2)"
            :key="item.id"
            @click="handleMenuClick(item)"
            class="flex-1 py-3 px-2 flex flex-col items-center"
            :class="[
              activeItem === item.id
                ? 'text-blue-600 dark:text-blue-400'
                : 'text-gray-600 dark:text-gray-400'
            ]"
          >
          <IconComponent :name="item.icon" />
            <span class="text-xs">{{ item.label }}</span>
          </button>
        </div>
      </div>
  
      <!-- Arc Menu Buttons -->
      <div
        v-if="showArcMenu"
        class="fixed inset-0 bg-black/50 z-50"
        @click="showArcMenu = false"
      >
        <div class="absolute bottom-20 left-4">
          <div class="relative">
            <!-- Arc Buttons -->
            <div
            v-for="button in arcButtons"
            :key="button.id"
            class="absolute"
          >
            <div class="flex flex-col items-center">
              <button
                @click="handleArcButtonClick(button)"
                class="w-14 h-14 rounded-full bg-white dark:bg-gray-800 shadow-lg flex items-center justify-center hover:bg-blue-50 dark:hover:bg-blue-900/50 hover:scale-110 transition-all"
                :style="{
                  transform: `translate(${button.x}px, ${button.y}px)`
                }"
              >
                <IconComponent :name="button.icon" />
              </button>
              <span 
                class="text-xs text-white font-medium whitespace-nowrap bg-black/30 px-2 py-1 rounded-full mt-2"
                :style="{
                  transform: `translate(${button.x}px, ${button.y}px)`
                }"
              >
                {{ button.label }}
              </span>
            </div>
          </div>
          </div>
        </div>
      </div>
  
      <!-- Plus Menu -->
      <div
        v-if="showPlusMenu"
        class="fixed inset-0 bg-black/50 z-50"
        @click="showPlusMenu = false"
      >
        <div class="absolute bottom-24 left-4 right-4">
          <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4">
            <div class="flex justify-between items-center space-x-2">
              <button
                v-for="button in plusMenuButtons"
                :key="button.id"
                @click="handlePlusMenuClick(button)"
                class="flex-1 flex flex-col items-center p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <IconComponent :name="button.icon" />
                <span class="text-xs text-gray-600 dark:text-gray-400">{{ button.label }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
  
      <!-- More Menu Drawer -->
      <div
        v-if="showDrawer"
        class="fixed inset-0 bg-black/50 z-50"
        @click="showDrawer = false"
      >
        <div
          class="absolute right-0 top-0 bottom-0 w-80 bg-white dark:bg-gray-900 shadow-xl"
          @click.stop
        >
          <!-- Drawer Header -->
          <div class="p-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Menu</h3>
            <button
              @click="showDrawer = false"
              class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              ✕
            </button>
          </div>
  
          <!-- Menu Groups -->
          <div class="overflow-y-auto h-[calc(100vh-64px)]">
            <div
              v-for="group in menuGroups"
              :key="group.id"
              class="border-b border-gray-200 dark:border-gray-800"
            >
              <!-- Group Header -->
              <button
                @click="toggleGroup(group.uid)"
                class="w-full px-4 py-3 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <div class="flex items-center gap-2">
                    <IconComponent :name="group.icon_css" />
                  <span class="font-medium text-gray-900 dark:text-white">{{ group.name }}</span>
                </div>
                <span
                  class="text-gray-500 transition-transform"
                  :class="{ 'rotate-180': expandedGroups.includes(group.uid) }"
                >
                  ▼
                </span>
              </button>
  
              <!-- Group Items -->
              <div v-show="expandedGroups.includes(group.uid)">
                <div
                  v-for="section in group.sub_menus"
                  :key="section.id"
                  class="px-4 py-2"
                >
                  <div class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                    {{ section.name }}
                  </div>
                  <div class="space-y-1">
                    <button
                      v-for="item in section.sub_menus"
                      :key="item.id"
                      @click="handleItemClick(item)"
                      class="w-full px-4 py-2 text-left text-sm rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-2"
                    >
                      <IconComponent :name="item.icon_css" />
                      <span class="text-gray-700 dark:text-gray-300">{{ item.name }}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    <SalesQuotationsForm
      v-if="showSalesQuotaionForm"
      end-point="revenue/sales/sales-quotations"
      title="Sales Quotations"
      mode="create"
      @save="showSalesQuotaionForm = false"
      @close="showSalesQuotaionForm = false"
    />
    <CustomersForm
      end-point="revenue/master-data/customers"
      title="Customers"
      v-if="showCustomerForm"
      mode="create"
      @save="showCustomerForm = false"
      @close="showCustomerForm = false"
    />
    <PaymentReceiptsForm
      end-point="finance/collections/payment-receipts"
      title="Payment Receipts"
      v-if="showPaymentForm"
      mode="create"
      @save="showPaymentForm = false"
      @close="showPaymentForm = false"
    />
    <!--TODO: Make these dynamic components
    -->
    </div>
  </template>
  
  <script setup lang="ts">
  import { onMounted, ref } from 'vue'
  import { useRouter } from 'vue-router'
  import { Menu } from '../../../types/auth'
import { tokenManager } from '../../../api/utils/tokenManager';
import { useWindowSize } from '@vueuse/core';
import IconComponent from '../../ui/common/IconComponent.vue';
import SalesQuotationsForm from '../../../features/revenue/sales/salesquotations/SalesQuotationsForm.vue';
import CustomersForm from '../../../features/revenue/masterdata/customers/CustomersForm.vue';
import PaymentReceiptsForm from '../../../features/finance/paymentreceipts/paymentreceipts/PaymentReceiptsForm.vue';
  
  onMounted(() => {
    updatePositions
  const mList = tokenManager.getMenu()
  const cat = mList?.filter((o) =>
    Object.keys(o).some(() =>
      o['modules_id'] == null
    )
  );
  cat?.forEach(c => {
    const cMenu = mList?.filter((o) =>
      Object.keys(o).some(() =>
        o['modules_id'] == c.id
      )
    );
    cMenu?.forEach(cm => {
        cm.sub_menus = (mList ?? []).filter((o) =>
            Object.keys(o).some(() =>
                o['modules_id'] == cm.id
            )
            );
    });
    c.sub_menus = cMenu ?? []
  })
  menuGroups.value = cat
});

  const router = useRouter()
  const showDrawer = ref(false)
  const showArcMenu = ref(false)
  const showPlusMenu = ref(false)
  const activeItem = ref('sales')
  const expandedGroups = ref<string[]>(['finance'])
  const menuGroups = ref<Menu[]>()

  const showSalesQuotaionForm = ref(false)
  const showCustomerForm = ref(false)
  const showPaymentForm = ref(false)

  // Plus menu buttons
  const plusMenuButtons = [
    { id: 'new-invoice', label: 'New Invoice', icon: 'Receipt', action: () => router.push('/revenue/sales/mobile-sales') },
    //{ id: 'new-order', label: 'New Order', icon: 'FileCheck', action: () => router.push('/orders/new') },
    { id: 'new-quote', label: 'New Quotation', icon: 'FileCheck', action: () => router.push('/quotes/new') },
    { id: 'new-payment', label: 'New Pyment', icon: 'Banknote', action: () => router.push('/products/new') },
    { id: 'new-customer', label: 'New Customer', icon: 'UserCircle', action: () => router.push('/customers/new') },    
  ]
  
  interface ArcButton {
    id: string;
    label: string;
    icon: string;
    path: string;
    x: number;
    y: number;
  }
  
  const arcButtons = ref<ArcButton[]>([])

  const arcSalesButtons = [
    { id: 'quotes', label: 'Quotations', icon: 'FileCheck', path: '/revenue/sales/sales-quotations', x: 0, y: -200 },
    { id: 'invoices', label: 'Invoices', icon: 'Receipt', path: '/revenue/sales/sales-invoices', x: 60, y: -150 },
    { id: 'delvierys', label: 'Delivery', icon: 'Truck', path: '/revenue/sales/delivery-trackings', x: 120, y: -100 },
    { id: 'customers', label: 'Customers', icon: 'UserCircle', path: '/revenue/master-data/customers', x: 150, y: -20 }
  ]

  const arcInventoryButtons = ref<ArcButton[]>([
    { id: 'items', label: 'Items', icon: 'Boxes', path: '/inventory/item-management/items', x: 310, y: -200 },
    { id: 'categories', label: 'Categories', icon: 'ChartBarStacked', path: '/inventory/item-management/categories', x: 240, y: -150 },
    { id: 'variants', label: 'Variants', icon: 'Shapes', path: '/inventory/item-management/variants', x: 180, y: -100 },
    { id: 'item-types', label: 'Item Types', icon: 'BookType', path: '/inventory/item-management/item-types', x: 150, y: -20 }
  ])
  
  const mainMenuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: 'LayoutDashboard', path: '/' },
    { id: 'sales', label: 'Sales', icon: 'ShoppingCart', path: '/sales' },
    { id: 'inventory', label: 'Inventory', icon: 'PackageSearch', path: '/inventory' },
    { id: 'more', label: 'More', icon: 'Ellipsis', path: null }
  ]
  
  
  const togglePlusMenu = () => {
    showPlusMenu.value = !showPlusMenu.value
  }
  
  const handlePlusMenuClick = (button: typeof plusMenuButtons[0]) => {
    showPlusMenu.value = false
    //button.action()
    if (button.id == 'new-invoice') 
    router.push('/revenue/sales/mobile-sales')
    else if (button.id == 'new-quote')
      showSalesQuotaionForm.value = true
    else if (button.id == 'new-customer')
      showCustomerForm.value = true
    else if (button.id == 'new-payment')
      showPaymentForm.value = true
  }
  
//  const arcButtons = ref([])
  const handleMenuClick = (item: typeof mainMenuItems[0]) => {
    if (item.id === 'more') {
      showDrawer.value = true
    } else if (item.id === 'sales') {
        arcButtons.value = arcSalesButtons
      showArcMenu.value = !showArcMenu.value
    }else if (item.id === 'inventory') {
        arcButtons.value = arcInventoryButtons.value
        showArcMenu.value = !showArcMenu.value
    } else {
      activeItem.value = item.id
      if (item.path) {
        router.push(item.path)
      }
    }
  }
  
  const handleArcButtonClick = (button: typeof arcSalesButtons[0]) => {
    showArcMenu.value = false
    if (button.path) {
      router.push(button.path)
    }
  }
  
  const toggleGroup = (groupId: string) => {
    const index = expandedGroups.value.indexOf(groupId)
    if (index === -1) {
      expandedGroups.value.push(groupId)
    } else {
      expandedGroups.value.splice(index, 1)
    }
  }
  
  const handleItemClick = (item: Menu) => {
    showDrawer.value = false
    if (item.route_path) {
      router.push(item.route_path.substring(0,1) == '/' ? item.route_path : '/' + item.route_path)
    }
  }

  const { width } = useWindowSize();
  const updatePositions = () => {
    const startX = width.value - 100; // Start from the right (adjust 100 for padding)
    arcInventoryButtons.value = arcInventoryButtons.value.map((btn, index) => ({
        ...btn,
        x: startX - index * 80 // Move each button to the left in steps
    }));
};
  </script>
  
  <style scoped>
  .arc-button-enter-active,
  .arc-button-leave-active {
    transition: transform 0.3s ease-out, opacity 0.3s ease-out;
  }
  
  .arc-button-enter-from,
  .arc-button-leave-to {
    opacity: 0;
    transform: translate(0, 0) scale(0.5) !important;
  }
  </style>